import {
  Box,
  ChevronDown,
  ChevronRight,
  Cloud,
  Globe2,
  Layers,
  Plus,
} from "lucide-react";

import { useRollouts } from "@/api/local/rollout";
import {
  diffWorkloadSpec,
  draftWorkloadOf,
  hasPendingDraft,
  liveWorkloadOf,
} from "@/api/local/workload";
import { PoolPhaseBadge, ReplicaPhaseBadge } from "@/components/PhaseBadge";
import { Pill, type PillTone } from "@/components/Pill";
import { HealthBar } from "@/components/HealthBar";
import type { Service } from "@/api/types";
import type { Focus } from "@/workloads/focus";
import {
  GLOBAL_TREE_KEY,
  humanCloudLabel,
  type NodeType,
  type PoolSublabelChip,
  type PodSublabelChip,
  type TreeNode,
  type TreeStats,
} from "@/workloads/tree-data";

interface TreeProps {
  roots: TreeNode[];
  focus: Focus;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onSelect: (node: TreeNode) => void;
  /**
   * Optional row-action: append a fresh placeholder pool to the clicked
   * service. Wired by `Workloads` to a `POST /services/{id}/pools`
   * mutation that drops a new row into the tree with safe defaults
   * (max_replicas=0) and jumps focus to its Settings tab so the
   * operator configures it in-place. Rendered as a small `+ pool`
   * button right after the service label so it stays visible regardless
   * of how narrow the tree pane is.
   */
  onAddPool?: (service: Service) => void;
  /**
   * Service id whose `+ pool` mutation is currently in flight. When set,
   * that service's button shows a pending label / disabled state; other
   * services' buttons remain clickable so the operator can fan out
   * placeholder pools across multiple services without serialising on
   * a single in-flight request.
   */
  addPoolPendingForServiceId?: number | null;
  /**
   * Optional row-action on the Global row: create a new draft service.
   * Same placement convention as `onAddPool` — appended right after the
   * row's label so it always stays in view.
   */
  onAddService?: () => void;
  /** True while the create-service mutation is in flight. */
  addServicePending?: boolean;
  loading?: boolean;
}

/**
 * Read-only tree of services / pools / pods.
 *
 * Renaming USED to live here as a per-row pencil button, but it was
 * easy to fat-finger when scanning the table. Renaming has moved to
 * the bottom pane's left menu (the SubNav title at the top of the
 * detail view) — see `SubNav` and `DetailShell`.
 */
export function Tree({
  roots,
  focus,
  expanded,
  onToggle,
  onSelect,
  onAddPool,
  addPoolPendingForServiceId,
  onAddService,
  addServicePending,
  loading,
}: TreeProps) {
  // The Global root is always present; the data we may still be loading is
  // the list of services hanging off of it.
  const hasServices = roots.some((r) =>
    r.type === "global" ? r.children.length > 0 : true,
  );

  return (
    <div className="tree" role="tree">
      <div className="tree__head">
        <div className="tree__head-cell" />
        <div className="tree__head-cell">Name</div>
        <div className="tree__head-cell tree__head-cell--num">Priority</div>
        <div className="tree__head-cell">Status</div>
        <div className="tree__head-cell tree__head-cell--num">Replicas</div>
        <div className="tree__head-cell tree__head-cell--num">$ / hour</div>
        <div className="tree__head-cell">Where</div>
        <div className="tree__head-cell" />
      </div>
      <div className="tree__body">
        {roots.map((node) => (
          <TreeBranch
            key={node.key}
            node={node}
            focus={focus}
            expanded={expanded}
            onToggle={onToggle}
            onSelect={onSelect}
            onAddPool={onAddPool}
            addPoolPendingForServiceId={addPoolPendingForServiceId}
            onAddService={onAddService}
            addServicePending={addServicePending}
          />
        ))}
        {!hasServices && (
          <div className="tree__hint">
            {loading ? (
              <span className="muted">Loading services…</span>
            ) : (
              <div className="empty-state">
                <div className="empty-state__title">No services yet</div>
                <div>
                  Create the first service via{" "}
                  <strong>+ service</strong> on the Global row to start
                  populating this tree.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface BranchProps {
  node: TreeNode;
  focus: Focus;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onSelect: (node: TreeNode) => void;
  onAddPool?: (service: Service) => void;
  addPoolPendingForServiceId?: number | null;
  onAddService?: () => void;
  addServicePending?: boolean;
}

function TreeBranch({
  node,
  focus,
  expanded,
  onToggle,
  onSelect,
  onAddPool,
  addPoolPendingForServiceId,
  onAddService,
  addServicePending,
}: BranchProps) {
  const isOpen = expanded.has(node.key);
  return (
    <>
      <TreeRow
        node={node}
        focus={focus}
        isOpen={isOpen}
        onToggle={onToggle}
        onSelect={onSelect}
        onAddPool={onAddPool}
        addPoolPendingForServiceId={addPoolPendingForServiceId}
        onAddService={onAddService}
        addServicePending={addServicePending}
      />
      {isOpen &&
        node.children.map((child) => (
          <TreeBranch
            key={child.key}
            node={child}
            focus={focus}
            expanded={expanded}
            onToggle={onToggle}
            onSelect={onSelect}
            onAddPool={onAddPool}
            addPoolPendingForServiceId={addPoolPendingForServiceId}
            onAddService={onAddService}
            addServicePending={addServicePending}
          />
        ))}
    </>
  );
}

interface RowProps {
  node: TreeNode;
  focus: Focus;
  isOpen: boolean;
  onToggle: (key: string) => void;
  onSelect: (node: TreeNode) => void;
  onAddPool?: (service: Service) => void;
  addPoolPendingForServiceId?: number | null;
  onAddService?: () => void;
  addServicePending?: boolean;
}

function TreeRow({
  node,
  focus,
  isOpen,
  onToggle,
  onSelect,
  onAddPool,
  addPoolPendingForServiceId,
  onAddService,
  addServicePending,
}: RowProps) {
  const focusKey = focusToKey(focus);
  const isSelected = focusKey === node.key;
  const hasChildren = node.children.length > 0;
  const Icon = iconFor(node);
  const tone = statusTone(node);
  const label = statusLabel(node);
  // `lifecycle_state` is part of the Service row itself, so re-fetching
  // /services through the parent query is enough to flip this; no
  // separate subscription is needed.
  const isServiceDraft =
    node.type === "service" && node.service?.lifecycle_state === "draft";

  return (
    <div
      role="treeitem"
      aria-expanded={hasChildren ? isOpen : undefined}
      aria-selected={isSelected}
      className={`tree__row tree__row--lvl-${node.level}${isSelected ? " is-selected" : ""}${isServiceDraft ? " is-draft" : ""}`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest(".tree__chevron")) return;
        onSelect(node);
      }}
    >
      <button
        type="button"
        className="tree__chevron"
        onClick={(e) => {
          e.stopPropagation();
          if (hasChildren) onToggle(node.key);
        }}
        aria-label={isOpen ? "Collapse" : "Expand"}
        disabled={!hasChildren}
      >
        {hasChildren ? (
          isOpen ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronRight size={14} />
          )
        ) : (
          <span className="tree__chevron-dot" aria-hidden="true" />
        )}
      </button>
      <div className="tree__name">
        <Icon className="tree__icon" aria-hidden="true" />
        <span className="tree__label">
          <span className="tree__title" title={node.label}>{node.label}</span>
          {node.sublabel && (
            <span className="tree__sub" title={node.sublabel}>
              {node.sublabel}
              {node.poolChips?.map((chip) => (
                <PoolChip key={`pool-${chip}`} chip={chip} />
              ))}
              {node.podChips?.map((chip) => (
                <PodChip key={`pod-${chip}`} chip={chip} />
              ))}
            </span>
          )}
          <RolloutBadge node={node} />
        </span>
        {node.type === "global" && onAddService && (
          <button
            type="button"
            className="btn btn--ghost btn--sm tree__row-action tree__row-action--inline"
            onClick={(e) => {
              e.stopPropagation();
              onAddService();
            }}
            disabled={addServicePending}
            title="Add a draft service to the tree; configure tabs, then roll out"
            aria-label="Add a new service"
          >
            <Plus />
            {addServicePending ? "creating…" : "service"}
          </button>
        )}
        {node.type === "service" && node.service && onAddPool && (
          <button
            type="button"
            className="btn btn--ghost btn--sm tree__row-action tree__row-action--inline"
            onClick={(e) => {
              // Don't let the row's onClick steal this and re-focus
              // the service detail when the new pool is appended.
              e.stopPropagation();
              onAddPool(node.service!);
            }}
            disabled={addPoolPendingForServiceId === node.service.id}
            title={`Add a pool to ${node.label}`}
            aria-label={`Add pool to ${node.label}`}
          >
            <Plus />
            {addPoolPendingForServiceId === node.service.id
              ? "creating…"
              : "pool"}
          </button>
        )}
      </div>
      <div className="tree__priority">
        {node.type === "pool" && node.pool ? (
          <Pill tone="info">P{node.pool.priority}</Pill>
        ) : null}
      </div>
      <div className="tree__status">
        {node.type === "pool" && node.pool ? (
          // Pool rows show the honest derived status chip (phase +
          // rejection/error evidence folded together server-side) so
          // a capacity-waiting launch reads "Launching — capacity wait 47m"
          // in amber at a glance — never a calm "Launching" that
          // hides an hour-long rejection loop.
          <PoolPhaseBadge
            phase={node.pool.phase}
            reason={node.pool.phase_reason}
            pool={node.pool}
            compact
            hideHelpLink
          />
        ) : node.type === "pod" && node.replica ? (
          <ReplicaPhaseBadge phase={node.replica.phase} compact hideHelpLink />
        ) : (
          <Pill tone={tone}>{label}</Pill>
        )}
      </div>
      <div className="tree__replicas">
        <HealthBar
          ready={node.stats.ready}
          pending={node.stats.pending}
          failed={node.stats.failed}
          total={node.stats.total}
          width={64}
        />
        <ReplicasReadout stats={node.stats} type={node.type} />
      </div>
      <div
        className="tree__cost mono num"
        title={
          node.stats.hourlyCostUnknown
            ? "No rate resolved for one or more billable replicas under this row — burn rate is unknown, not zero."
            : node.stats.hourlyCost > 0
              ? "Measured burn rate: the sum of the open cost-ledger intervals under this row. Intervals close when the provider stops reporting the instance."
              : undefined
        }
      >
        {node.stats.hourlyCostUnknown
          ? "—"
          : node.stats.hourlyCost > 0
            ? `$${node.stats.hourlyCost.toFixed(node.type === "pod" ? 3 : 2)}`
            : "—"}
      </div>
      <div className="tree__where">
        <WhereCell node={node} />
      </div>
      <div className="tree__tail muted mono">
        {node.type === "pod" && node.podInternalIp && (
          <span title={`internal ${node.podInternalIp}`}>
            {truncate(node.podInternalIp, 16)}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * "Where is this running?" cell rendered on every tree row.
 *
 *   - Pod rows: cloud Pill + muted region. Renders "—" while the
 *     reconciler hasn't yet recorded a cloud (early provisioning).
 *   - Pool rows: one Pill per distinct cloud across the pool's
 *     replicas. Empty until at least one replica has been placed.
 *   - Service / Global rows: nothing (the cell is reserved space so
 *     the column grid stays aligned).
 */
function WhereCell({ node }: { node: TreeNode }) {
  if (node.type === "pod") {
    const loc = node.podLocation;
    if (!loc || (!loc.cloud && !loc.region)) {
      return <span className="muted">—</span>;
    }
    return (
      <span className="tree__where-inner">
        {loc.cloud && (
          <Pill tone="brand">{humanCloudLabel(loc.cloud)}</Pill>
        )}
        {loc.region && (
          <span className="muted mono tree__where-region">{loc.region}</span>
        )}
      </span>
    );
  }
  if (node.type === "pool") {
    const clouds = node.poolClouds ?? [];
    if (clouds.length === 0) return null;
    return (
      <span className="tree__where-inner">
        {clouds.map((c) => (
          <Pill key={c} tone="brand">
            {humanCloudLabel(c)}
          </Pill>
        ))}
      </span>
    );
  }
  return null;
}

/**
 * Inline pill that surfaces rollout / pending-change state on service
 * and pool rows. Subscribes directly to the workload + rollout stores so
 * the tree refreshes without prop drilling.
 */
function RolloutBadge({ node }: { node: TreeNode }) {
  const rollouts = useRollouts();

  if (node.type === "service") {
    const serviceId = node.id;
    if (serviceId === null) return null;
    const active = rollouts.active;
    const isActive =
      active && active.scope === "service" && active.service_id === serviceId;
    if (isActive) {
      return (
        <Pill tone="info" pulse>
          rolling out
        </Pill>
      );
    }
    if (node.service?.lifecycle_state === "draft") {
      // Draft services have nothing live yet — surface that loud and
      // clear so the operator knows clicking around is safe.
      return <Pill tone="info">draft</Pill>;
    }
    // "Pending" = the service has a workload_draft that differs from
    // live. We read both straight off the Service row already attached
    // to the node so this doesn't trigger a per-row API call.
    if (node.service && hasPendingDraft(node.service)) {
      const live = liveWorkloadOf(node.service);
      const draft = draftWorkloadOf(node.service);
      const pending = diffWorkloadSpec(live, draft).length;
      if (pending > 0) {
        return <Pill tone="warn">{pending} pending</Pill>;
      }
    }
    return null;
  }

  if (node.type === "pool") {
    const poolId = node.id;
    if (poolId === null) return null;
    const active = rollouts.active;
    const isActive =
      active && active.scope === "pool" && active.pool_id === poolId;
    if (isActive) {
      return (
        <Pill tone="info" pulse>
          rolling out
        </Pill>
      );
    }
    return null;
  }

  return null;
}

function focusToKey(focus: Focus): string {
  if (focus.type === "global" || focus.id === null) return GLOBAL_TREE_KEY;
  return `${focus.type}:${focus.id}`;
}

function iconFor(node: TreeNode) {
  switch (node.type) {
    case "global":
      return Globe2;
    case "service":
      return Layers;
    case "pool":
      return Cloud;
    case "pod":
      return Box;
  }
}

const POOL_CHIP_LABELS: Record<PoolSublabelChip, string> = {
  cascade: "cascade",
  slo: "SLO",
};

const POOL_CHIP_TONES: Record<PoolSublabelChip, PillTone> = {
  cascade: "info",
  slo: "warn",
};

function PoolChip({ chip }: { chip: PoolSublabelChip }) {
  return (
    <Pill tone={POOL_CHIP_TONES[chip]}>{POOL_CHIP_LABELS[chip]}</Pill>
  );
}

const POD_CHIP_LABELS: Record<PodSublabelChip, string> = {
  "ws-only": "WS-only",
  draining: "draining",
  "shutting-down": "shutting down",
};

const POD_CHIP_TONES: Record<PodSublabelChip, PillTone> = {
  "ws-only": "info",
  draining: "warn",
  "shutting-down": "neutral",
};

function PodChip({ chip }: { chip: PodSublabelChip }) {
  return (
    <Pill tone={POD_CHIP_TONES[chip]}>{POD_CHIP_LABELS[chip]}</Pill>
  );
}

function statusTone(node: TreeNode): PillTone {
  if (node.type === "pod") {
    if (!node.replica) return "neutral";
    switch (node.replica.status) {
      case "ready":
        return "ok";
      case "pending":
      case "provisioning":
        return "warn";
      case "draining_long_lived":
      case "draining":
        return "info";
      case "shutting_down":
        return "neutral";
      case "failed":
      case "not_ready":
        return "danger";
      default:
        return "neutral";
    }
  }
  if (node.stats.total === 0) return "neutral";
  if (node.stats.failed > 0) return "danger";
  if (node.stats.pending > 0) return "warn";
  if (node.stats.ready === node.stats.total) return "ok";
  return "warn";
}

function statusLabel(node: TreeNode): string {
  if (node.type === "pod") {
    return node.replica?.status ?? "unknown";
  }
  if (node.stats.total === 0) return "idle";
  if (node.stats.failed > 0) return "degraded";
  if (node.stats.pending > 0) return "scaling";
  // Replicas that aren't ready, pending, or failed must be in a
  // transitional drain state (`shutting_down` / `draining` /
  // `draining_long_lived`) — they still count toward `total` but
  // contribute to none of the sub-buckets. Falling straight to
  // "healthy" here lied to the operator (e.g. a pool showing
  // "healthy 0/2" while every replica is shutting down during a
  // rollout); surface "draining" instead so the label tracks the
  // tone, which already correctly degrades to "warn" in this case.
  if (node.stats.ready < node.stats.total) return "draining";
  return "healthy";
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

/**
 * "live / cap" replica readout shown next to the HealthBar.
 *
 * The numerator is the count of replica rows that still consume capacity
 * (`stats.total - stats.shutting`) — i.e. ready, provisioning, failed, and
 * the drain stages that are still serving traffic (`draining_long_lived`,
 * `draining`). Replicas already in `shutting_down` are excluded because
 * SkyServe is actively tearing them down and they no longer hold a slot
 * against the cap; counting them previously surfaced bogus "+N over cap"
 * warnings during routine drains.
 *
 * The denominator is the configured cap (`stats.desired` = `pool.max_replicas`,
 * aggregated across pools for service / global rows).
 *
 * We deliberately key the ratio off `live/desired` rather than `ready/total`
 * so the typical rollout overshoot (SkyServe's ROLLING update brings up the
 * new version before tearing down the old one — old replicas sit in
 * `draining*` stages, which still count as live) reads as `4/2` — clearly
 * "above cap" — instead of `0/4 · cap 2`, which made the inflated row count
 * look like the requested target. The over-cap numerator gets a `warn`
 * highlight so the eye lands on it immediately. Hover for the
 * ready / provisioning / failed / shutting-down split.
 *
 * Pod rows have no cap of their own (they're a single replica's view) so we
 * keep the legacy `ready/total` form there.
 */
function ReplicasReadout({
  stats,
  type,
}: {
  stats: TreeStats;
  type: NodeType;
}) {
  if (type === "pod" || stats.desired === 0) {
    return (
      <span className="mono num tree__replicas-text">
        {stats.ready}/{stats.total}
      </span>
    );
  }
  const live = stats.total - stats.shutting;
  const overCap = live > stats.desired;
  return (
    <span
      className="mono num tree__replicas-text"
      title={
        `${stats.ready} ready · ${stats.pending} provisioning · ` +
        `${stats.failed} failed · ${stats.shutting} shutting down · ` +
        `cap ${stats.desired}` +
        (overCap ? ` · +${live - stats.desired} over cap (rollout surge)` : "")
      }
    >
      <span
        className={overCap ? "tree__replicas-text--over" : undefined}
      >
        {live}
      </span>
      /{stats.desired}
    </span>
  );
}
